import React, { useState, useEffect, useRef } from 'react';
import {
    X,
    ChevronRight,
    ChevronLeft,
    Target,
    Sparkles,
    Lightbulb,
    Check,
    Loader2,
    DollarSign,
    Users,
    TrendingUp,
    Compass,
    GraduationCap,
    Camera,
    Package,
    Tag,
    MessageSquare,
    Heart,
    Zap
} from 'lucide-react';
import { useApp } from '../../contexts/AppContext';
import { useAuth } from '../../contexts/AuthContext';
import { suggestPillars } from '../../services/strategyAIService';
import { createPillar, PILLAR_PRESETS } from '../../services/pillarService';
import { updateStrategy, initializeStrategy } from '../../services/strategyService';

/**
 * Icon mapping for pillar icons
 */
const ICON_MAP = {
    GraduationCap,
    Sparkles,
    Camera,
    Package,
    Tag,
    Lightbulb,
    Target,
    MessageSquare,
    Heart,
    Users,
    Zap,
    TrendingUp
};

/**
 * Business challenge options
 */
const CHALLENGE_OPTIONS = [
    {
        id: 'new_customers',
        label: 'Acquire New Customers',
        description: 'Focus on reaching new audiences and growing customer base',
        icon: Users
    },
    {
        id: 'repeat_purchases',
        label: 'Drive Repeat Purchases',
        description: 'Increase customer loyalty and lifetime value',
        icon: Heart
    },
    {
        id: 'brand_building',
        label: 'Build Brand Awareness',
        description: 'Establish brand presence and recognition',
        icon: TrendingUp
    },
    {
        id: 'market_expansion',
        label: 'Expand to New Markets',
        description: 'Enter new geographical or demographic markets',
        icon: Compass
    }
];

/**
 * StrategySetupWizard Component
 * Guided onboarding for setting up marketing strategy and pillars
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the wizard is open
 * @param {function} props.onClose - Callback to close the wizard
 * @param {function} props.onComplete - Callback when setup is complete
 */
const StrategySetupWizard = ({ isOpen, onClose, onComplete }) => {
    const { selectedBrand } = useApp();
    const { currentUser } = useAuth();

    // Wizard steps
    const [currentStep, setCurrentStep] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [error, setError] = useState(null);

    // Use ref to track completion - prevents re-renders that cause flash
    const hasCompletedRef = useRef(false);

    // Step 1: Business Goals
    const [businessGoals, setBusinessGoals] = useState({
        monthlyRevenueTarget: '',
        averageOrderValue: '',
        customerLifetimeValue: '',
        currentChallenge: ''
    });

    // Step 2: AI Pillar Suggestions
    const [suggestedPillars, setSuggestedPillars] = useState([]);
    const [selectedPillars, setSelectedPillars] = useState([]);
    const [isGeneratingSuggestions, setIsGeneratingSuggestions] = useState(false);

    // Additional pillar generation
    const [additionalPillarRequest, setAdditionalPillarRequest] = useState('');
    const [isGeneratingMore, setIsGeneratingMore] = useState(false);

    // Step 3: Customize Pillars
    const [customizedPillars, setCustomizedPillars] = useState([]);

    const STEPS = [
        { title: 'Business Goals', icon: Target },
        { title: 'Content Pillars', icon: Lightbulb },
        { title: 'Customize', icon: Sparkles }
    ];

    // Track previous isOpen value with ref (doesn't cause re-renders)
    const prevIsOpenRef = useRef(false);

    // Reset wizard only when freshly opened
    useEffect(() => {
        // Only reset when transitioning from closed to open
        if (isOpen && !prevIsOpenRef.current) {
            console.log('[StrategySetupWizard] Resetting wizard state (freshly opened)');
            hasCompletedRef.current = false;
            setCurrentStep(0);
            setBusinessGoals({
                monthlyRevenueTarget: '',
                averageOrderValue: '',
                customerLifetimeValue: '',
                currentChallenge: ''
            });
            setSuggestedPillars([]);
            setSelectedPillars([]);
            setCustomizedPillars([]);
            setError(null);
        }
        prevIsOpenRef.current = isOpen;
    }, [isOpen]);

    // Generate pillar suggestions when entering step 1
    const generatePillarSuggestions = async () => {
        if (!selectedBrand) return;

        console.log('[StrategySetupWizard] Generating pillar suggestions for:', selectedBrand.name);
        setIsGeneratingSuggestions(true);
        setError(null);

        try {
            const suggestions = await suggestPillars(selectedBrand, {
                revenueTarget: businessGoals.monthlyRevenueTarget,
                challenge: businessGoals.currentChallenge
            });

            console.log('[StrategySetupWizard] Generated', suggestions.length, 'pillar suggestions');
            setSuggestedPillars(suggestions);
            // Pre-select all AI suggestions
            setSelectedPillars(suggestions.map((_, idx) => idx));
        } catch (err) {
            console.error('Error generating pillar suggestions:', err);
            // Fall back to presets if AI fails
            const presetPillars = Object.values(PILLAR_PRESETS).slice(0, 4).map(preset => ({
                name: preset.name,
                description: preset.description,
                icon: preset.icon,
                color: preset.color,
                awarenessMapping: preset.awarenessMapping,
                funnelMapping: preset.funnelMapping,
                contentGuidelines: preset.contentGuidelines
            }));
            console.log('[StrategySetupWizard] Using fallback presets');
            setSuggestedPillars(presetPillars);
            setSelectedPillars([0, 1, 2, 3]);
        } finally {
            setIsGeneratingSuggestions(false);
        }
    };

    // Generate additional pillars based on user request
    const generateMorePillars = async () => {
        if (!selectedBrand || !additionalPillarRequest.trim()) return;

        setIsGeneratingMore(true);
        setError(null);

        try {
            const additionalSuggestions = await suggestPillars(selectedBrand, {
                revenueTarget: businessGoals.monthlyRevenueTarget,
                challenge: businessGoals.currentChallenge,
                customRequest: additionalPillarRequest.trim(),
                existingPillars: suggestedPillars.map(p => p.name)
            });

            // Append new pillars to existing ones
            const currentLength = suggestedPillars.length;
            setSuggestedPillars(prev => [...prev, ...additionalSuggestions]);

            // Auto-select the new pillars
            const newIndices = additionalSuggestions.map((_, idx) => currentLength + idx);
            setSelectedPillars(prev => [...prev, ...newIndices]);

            // Clear the request input
            setAdditionalPillarRequest('');
        } catch (err) {
            console.error('Error generating additional pillars:', err);
            setError('Failed to generate additional pillars. Please try again.');
        } finally {
            setIsGeneratingMore(false);
        }
    };

    // Handle step navigation
    const handleNext = async () => {
        // Prevent rapid clicks or transitions while already transitioning
        if (isTransitioning || isLoading || isGeneratingSuggestions) {
            console.log('[StrategySetupWizard] Blocked transition - already transitioning');
            return;
        }

        setError(null);
        console.log('[StrategySetupWizard] handleNext called, currentStep:', currentStep);

        if (currentStep === 0) {
            // Validate business goals
            if (!businessGoals.currentChallenge) {
                setError('Please select your current business challenge');
                return;
            }
            // Move to step 1 and generate suggestions
            setIsTransitioning(true);
            setCurrentStep(1);
            await generatePillarSuggestions();
            setIsTransitioning(false);
            return;
        }

        if (currentStep === 1) {
            // Validate pillar selection
            if (selectedPillars.length === 0) {
                setError('Please select at least one content pillar');
                return;
            }

            // Validate that suggestions are loaded
            if (!suggestedPillars || suggestedPillars.length === 0) {
                setError('Pillar suggestions are still loading. Please wait.');
                return;
            }

            // Prepare customized pillars with validation
            const pillarsToCustomize = selectedPillars
                .map(idx => suggestedPillars[idx])
                .filter(pillar => pillar && pillar.name) // Filter out invalid pillars
                .map(pillar => ({
                    ...pillar,
                    frequency: { currentWeight: Math.floor(100 / selectedPillars.length), postsPerWeek: 2 }
                }));

            if (pillarsToCustomize.length === 0) {
                setError('No valid pillars selected. Please try again.');
                return;
            }

            console.log('[StrategySetupWizard] Step 1 → 2: customizing', pillarsToCustomize.length, 'pillars');
            setIsTransitioning(true);
            setCustomizedPillars(pillarsToCustomize);
            // Use setTimeout to ensure state is set before transitioning
            setTimeout(() => {
                setCurrentStep(2);
                setIsTransitioning(false);
                console.log('[StrategySetupWizard] Transitioned to step 2');
            }, 50);
            return;
        }

        if (currentStep === 2) {
            // Validate customized pillars exist
            if (!customizedPillars || customizedPillars.length === 0) {
                setError('No pillars to save. Please go back and select pillars.');
                return;
            }
            console.log('[StrategySetupWizard] Step 2 → Complete: saving', customizedPillars.length, 'pillars');
            // Save pillars and complete setup
            await handleComplete();
            return;
        }
    };

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
            setError(null);
        }
    };

    // Toggle pillar selection
    const togglePillarSelection = (index) => {
        setSelectedPillars(prev => {
            if (prev.includes(index)) {
                return prev.filter(i => i !== index);
            } else {
                return [...prev, index];
            }
        });
    };

    // Update customized pillar
    const updateCustomizedPillar = (index, field, value) => {
        setCustomizedPillars(prev => prev.map((pillar, i) => {
            if (i === index) {
                if (field.includes('.')) {
                    const [parent, child] = field.split('.');
                    return {
                        ...pillar,
                        [parent]: { ...pillar[parent], [child]: value }
                    };
                }
                return { ...pillar, [field]: value };
            }
            return pillar;
        }));
    };

    // Complete setup and save
    const handleComplete = async () => {
        if (!currentUser || !selectedBrand) {
            console.error('[StrategySetupWizard] Cannot complete - missing user or brand');
            return;
        }

        console.log('[StrategySetupWizard] Starting handleComplete with', customizedPillars.length, 'pillars');
        hasCompletedRef.current = true; // Mark as completing to prevent any re-renders showing the form
        setIsLoading(true);
        setError(null);

        try {
            // Initialize brand strategy
            console.log('[StrategySetupWizard] Initializing strategy...');
            await initializeStrategy(selectedBrand.id);

            // Update business goals
            console.log('[StrategySetupWizard] Updating business goals...');
            await updateStrategy(selectedBrand.id, {
                businessGoals: {
                    monthlyRevenueTarget: parseFloat(businessGoals.monthlyRevenueTarget) || 0,
                    averageOrderValue: parseFloat(businessGoals.averageOrderValue) || 0,
                    customerLifetimeValue: parseFloat(businessGoals.customerLifetimeValue) || 0,
                    currentChallenge: businessGoals.currentChallenge
                },
                setupComplete: true
            });

            // Create pillars
            console.log('[StrategySetupWizard] Creating pillars...');
            for (const pillar of customizedPillars) {
                await createPillar({
                    name: pillar.name,
                    description: pillar.description,
                    icon: pillar.icon,
                    color: pillar.color,
                    awarenessMapping: pillar.awarenessMapping,
                    funnelMapping: pillar.funnelMapping,
                    contentGuidelines: pillar.contentGuidelines,
                    frequency: pillar.frequency
                }, currentUser.uid, selectedBrand.id);
            }

            console.log('[StrategySetupWizard] Setup complete! Closing wizard.');
            setIsLoading(false);
            // Close immediately after successful save
            onComplete?.();
        } catch (err) {
            console.error('[StrategySetupWizard] Error completing strategy setup:', err);
            setError('Failed to save strategy. Please try again.');
            setIsLoading(false);
            hasCompletedRef.current = false; // Allow retry
        }
    };

    // Don't render if closed or if we've completed (prevents flash on close)
    if (!isOpen || hasCompletedRef.current) return null;

    return (
        <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
        }}>
            <div style={{
                background: 'var(--color-bg-primary)',
                borderRadius: 'var(--radius-xl)',
                width: '90%',
                maxWidth: '800px',
                maxHeight: '90vh',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
            }}>
                {/* Header */}
                <div style={{
                    padding: 'var(--space-4) var(--space-6)',
                    borderBottom: '1px solid var(--color-border)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <div>
                        <h2 style={{ margin: 0, color: 'var(--color-text-primary)' }}>Strategy Setup</h2>
                        <p style={{ margin: '4px 0 0', color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
                            Set up your marketing strategy for {selectedBrand?.name}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="btn btn-ghost btn-sm"
                        style={{ padding: 'var(--space-2)' }}
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Progress Steps */}
                <div style={{
                    padding: 'var(--space-4) var(--space-6)',
                    background: 'var(--color-bg-secondary)',
                    display: 'flex',
                    justifyContent: 'space-between'
                }}>
                    {STEPS.map((step, idx) => {
                        const Icon = step.icon;
                        const isActive = idx === currentStep;
                        const isComplete = idx < currentStep;

                        return (
                            <div
                                key={idx}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-2)',
                                    opacity: isActive || isComplete ? 1 : 0.5
                                }}
                            >
                                <div style={{
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '50%',
                                    background: isComplete ? 'var(--color-success)' : isActive ? 'var(--color-primary)' : 'var(--color-bg-tertiary)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: isComplete || isActive ? 'white' : 'var(--color-text-tertiary)'
                                }}>
                                    {isComplete ? <Check size={16} /> : <Icon size={16} />}
                                </div>
                                <span style={{
                                    fontSize: 'var(--font-size-sm)',
                                    fontWeight: isActive ? 600 : 400,
                                    color: 'var(--color-text-primary)'
                                }}>
                                    {step.title}
                                </span>
                                {idx < STEPS.length - 1 && (
                                    <ChevronRight size={16} style={{ color: 'var(--color-text-tertiary)', marginLeft: 'var(--space-2)' }} />
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Content */}
                <div style={{
                    padding: 'var(--space-6)',
                    flex: 1,
                    overflowY: 'auto'
                }}>
                    {/* Step 1: Business Goals */}
                    {currentStep === 0 && (
                        <div>
                            <h3 style={{ marginBottom: 'var(--space-4)', color: 'var(--color-text-primary)' }}>Define Your Business Goals</h3>
                            <p style={{ color: 'var(--color-text-secondary)', marginBottom: 'var(--space-6)' }}>
                                Help us understand your revenue targets so we can recommend the right content strategy.
                            </p>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)', marginBottom: 'var(--space-6)' }}>
                                <div>
                                    <label style={{ display: 'block', marginBottom: 'var(--space-1)', fontSize: 'var(--font-size-sm)', fontWeight: 500, color: 'var(--color-text-primary)' }}>
                                        Monthly Revenue Target
                                    </label>
                                    <div style={{ position: 'relative' }}>
                                        <DollarSign size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-tertiary)' }} />
                                        <input
                                            type="number"
                                            placeholder="50000"
                                            value={businessGoals.monthlyRevenueTarget}
                                            onChange={(e) => setBusinessGoals({ ...businessGoals, monthlyRevenueTarget: e.target.value })}
                                            style={{
                                                width: '100%',
                                                padding: 'var(--space-2) var(--space-3) var(--space-2) 36px',
                                                border: '1px solid var(--color-border)',
                                                borderRadius: 'var(--radius-md)',
                                                background: 'var(--color-bg-secondary)',
                                                color: 'var(--color-text-primary)'
                                            }}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label style={{ display: 'block', marginBottom: 'var(--space-1)', fontSize: 'var(--font-size-sm)', fontWeight: 500, color: 'var(--color-text-primary)' }}>
                                        Average Order Value
                                    </label>
                                    <div style={{ position: 'relative' }}>
                                        <DollarSign size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-tertiary)' }} />
                                        <input
                                            type="number"
                                            placeholder="100"
                                            value={businessGoals.averageOrderValue}
                                            onChange={(e) => setBusinessGoals({ ...businessGoals, averageOrderValue: e.target.value })}
                                            style={{
                                                width: '100%',
                                                padding: 'var(--space-2) var(--space-3) var(--space-2) 36px',
                                                border: '1px solid var(--color-border)',
                                                borderRadius: 'var(--radius-md)',
                                                background: 'var(--color-bg-secondary)',
                                                color: 'var(--color-text-primary)'
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div style={{ marginBottom: 'var(--space-6)' }}>
                                <label style={{ display: 'block', marginBottom: 'var(--space-1)', fontSize: 'var(--font-size-sm)', fontWeight: 500, color: 'var(--color-text-primary)' }}>
                                    Customer Lifetime Value (optional)
                                </label>
                                <div style={{ position: 'relative', maxWidth: '300px' }}>
                                    <DollarSign size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-tertiary)' }} />
                                    <input
                                        type="number"
                                        placeholder="500"
                                        value={businessGoals.customerLifetimeValue}
                                        onChange={(e) => setBusinessGoals({ ...businessGoals, customerLifetimeValue: e.target.value })}
                                        style={{
                                            width: '100%',
                                            padding: 'var(--space-2) var(--space-3) var(--space-2) 36px',
                                            border: '1px solid var(--color-border)',
                                            borderRadius: 'var(--radius-md)',
                                            background: 'var(--color-bg-secondary)',
                                            color: 'var(--color-text-primary)'
                                        }}
                                    />
                                </div>
                            </div>

                            <div>
                                <label style={{ display: 'block', marginBottom: 'var(--space-2)', fontSize: 'var(--font-size-sm)', fontWeight: 500, color: 'var(--color-text-primary)' }}>
                                    What's your biggest challenge right now? *
                                </label>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
                                    {CHALLENGE_OPTIONS.map(option => {
                                        const Icon = option.icon;
                                        const isSelected = businessGoals.currentChallenge === option.id;

                                        return (
                                            <button
                                                key={option.id}
                                                type="button"
                                                onClick={() => setBusinessGoals({ ...businessGoals, currentChallenge: option.id })}
                                                style={{
                                                    padding: 'var(--space-4)',
                                                    border: `2px solid ${isSelected ? 'var(--color-primary)' : 'var(--color-border)'}`,
                                                    borderRadius: 'var(--radius-lg)',
                                                    background: isSelected ? 'var(--color-primary-light)' : 'var(--color-bg-primary)',
                                                    cursor: 'pointer',
                                                    textAlign: 'left',
                                                    transition: 'all var(--transition-fast)'
                                                }}
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                                                    <Icon size={18} style={{ color: isSelected ? 'var(--color-primary)' : 'var(--color-text-secondary)' }} />
                                                    <span style={{ fontWeight: 500, color: 'var(--color-text-primary)' }}>{option.label}</span>
                                                </div>
                                                <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                                                    {option.description}
                                                </p>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 2: Content Pillars */}
                    {currentStep === 1 && (
                        <div>
                            <h3 style={{ marginBottom: 'var(--space-4)', color: 'var(--color-text-primary)' }}>Choose Your Content Pillars</h3>
                            <p style={{ color: 'var(--color-text-secondary)', marginBottom: 'var(--space-6)' }}>
                                Based on your goals, we recommend these content pillars. Select the ones that fit your brand.
                            </p>

                            {isGeneratingSuggestions ? (
                                <div style={{ textAlign: 'center', padding: 'var(--space-8)' }}>
                                    <Loader2 size={32} className="animate-spin" style={{ color: 'var(--color-primary)', marginBottom: 'var(--space-4)' }} />
                                    <p style={{ color: 'var(--color-text-secondary)' }}>Generating personalized recommendations...</p>
                                </div>
                            ) : (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 'var(--space-3)' }}>
                                    {suggestedPillars.map((pillar, idx) => {
                                        const Icon = ICON_MAP[pillar.icon] || Lightbulb;
                                        const isSelected = selectedPillars.includes(idx);

                                        return (
                                            <button
                                                key={idx}
                                                type="button"
                                                onClick={() => togglePillarSelection(idx)}
                                                style={{
                                                    padding: 'var(--space-4)',
                                                    border: `2px solid ${isSelected ? pillar.color : 'var(--color-border)'}`,
                                                    borderRadius: 'var(--radius-lg)',
                                                    background: isSelected ? `${pillar.color}10` : 'var(--color-bg-primary)',
                                                    cursor: 'pointer',
                                                    textAlign: 'left',
                                                    transition: 'all var(--transition-fast)',
                                                    position: 'relative'
                                                }}
                                            >
                                                {isSelected && (
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: '8px',
                                                        right: '8px',
                                                        width: '20px',
                                                        height: '20px',
                                                        borderRadius: '50%',
                                                        background: pillar.color,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}>
                                                        <Check size={12} color="white" />
                                                    </div>
                                                )}
                                                <div style={{
                                                    width: '40px',
                                                    height: '40px',
                                                    borderRadius: 'var(--radius-md)',
                                                    background: `${pillar.color}20`,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    marginBottom: 'var(--space-3)'
                                                }}>
                                                    <Icon size={20} style={{ color: pillar.color }} />
                                                </div>
                                                <h4 style={{ margin: '0 0 var(--space-1) 0', color: pillar.color }}>
                                                    {pillar.name}
                                                </h4>
                                                <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                                                    {pillar.description}
                                                </p>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Generate More Pillars Section */}
                            {!isGeneratingSuggestions && suggestedPillars.length > 0 && (
                                <div style={{
                                    marginTop: 'var(--space-6)',
                                    padding: 'var(--space-4)',
                                    background: 'var(--color-bg-secondary)',
                                    borderRadius: 'var(--radius-lg)',
                                    border: '1px dashed var(--color-border)'
                                }}>
                                    <p style={{
                                        margin: '0 0 var(--space-3) 0',
                                        fontSize: 'var(--font-size-sm)',
                                        fontWeight: 500,
                                        color: 'var(--color-text-primary)'
                                    }}>
                                        Need different pillars?
                                    </p>
                                    <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                                        <input
                                            type="text"
                                            placeholder="e.g., behind-the-scenes content, sustainability focus, humor/memes..."
                                            value={additionalPillarRequest}
                                            onChange={(e) => setAdditionalPillarRequest(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && additionalPillarRequest.trim() && !isGeneratingMore) {
                                                    generateMorePillars();
                                                }
                                            }}
                                            style={{
                                                flex: 1,
                                                padding: 'var(--space-2) var(--space-3)',
                                                border: '1px solid var(--color-border)',
                                                borderRadius: 'var(--radius-md)',
                                                background: 'var(--color-bg-primary)',
                                                color: 'var(--color-text-primary)',
                                                fontSize: 'var(--font-size-sm)'
                                            }}
                                            disabled={isGeneratingMore}
                                        />
                                        <button
                                            type="button"
                                            onClick={generateMorePillars}
                                            disabled={!additionalPillarRequest.trim() || isGeneratingMore}
                                            className="btn btn-secondary"
                                            style={{ whiteSpace: 'nowrap' }}
                                        >
                                            {isGeneratingMore ? (
                                                <>
                                                    <Loader2 size={16} className="animate-spin" />
                                                    Generating...
                                                </>
                                            ) : (
                                                <>
                                                    <Sparkles size={16} />
                                                    Generate More
                                                </>
                                            )}
                                        </button>
                                    </div>
                                    <p style={{
                                        margin: 'var(--space-2) 0 0 0',
                                        fontSize: 'var(--font-size-xs)',
                                        color: 'var(--color-text-tertiary)'
                                    }}>
                                        Describe what type of content pillars you're looking for and we'll generate more options.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Step 3: Customize Pillars */}
                    {currentStep === 2 && (
                        <div>
                            <h3 style={{ marginBottom: 'var(--space-4)', color: 'var(--color-text-primary)' }}>Customize Your Pillars</h3>
                            <p style={{ color: 'var(--color-text-secondary)', marginBottom: 'var(--space-6)' }}>
                                Fine-tune each pillar to match your brand voice and content strategy.
                            </p>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                                {customizedPillars.map((pillar, idx) => {
                                    const Icon = ICON_MAP[pillar.icon] || Lightbulb;

                                    return (
                                        <div
                                            key={idx}
                                            style={{
                                                padding: 'var(--space-4)',
                                                border: '1px solid var(--color-border)',
                                                borderRadius: 'var(--radius-lg)',
                                                borderLeft: `4px solid ${pillar.color}`
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
                                                <div style={{
                                                    width: '36px',
                                                    height: '36px',
                                                    borderRadius: 'var(--radius-md)',
                                                    background: `${pillar.color}20`,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}>
                                                    <Icon size={18} style={{ color: pillar.color }} />
                                                </div>
                                                <input
                                                    type="text"
                                                    value={pillar.name}
                                                    onChange={(e) => updateCustomizedPillar(idx, 'name', e.target.value)}
                                                    style={{
                                                        flex: 1,
                                                        padding: 'var(--space-2)',
                                                        border: '1px solid var(--color-border)',
                                                        borderRadius: 'var(--radius-md)',
                                                        fontSize: 'var(--font-size-base)',
                                                        fontWeight: 600,
                                                        background: 'var(--color-bg-secondary)',
                                                        color: 'var(--color-text-primary)'
                                                    }}
                                                />
                                            </div>

                                            <div style={{ marginBottom: 'var(--space-3)' }}>
                                                <label style={{ display: 'block', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)', marginBottom: '4px' }}>
                                                    Description
                                                </label>
                                                <textarea
                                                    value={pillar.description}
                                                    onChange={(e) => updateCustomizedPillar(idx, 'description', e.target.value)}
                                                    rows={2}
                                                    style={{
                                                        width: '100%',
                                                        padding: 'var(--space-2)',
                                                        border: '1px solid var(--color-border)',
                                                        borderRadius: 'var(--radius-md)',
                                                        fontSize: 'var(--font-size-sm)',
                                                        resize: 'none',
                                                        background: 'var(--color-bg-secondary)',
                                                        color: 'var(--color-text-primary)'
                                                    }}
                                                />
                                            </div>

                                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
                                                <div style={{ flex: 1 }}>
                                                    <label style={{ display: 'block', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)', marginBottom: '4px' }}>
                                                        Content Weight (%)
                                                    </label>
                                                    <input
                                                        type="range"
                                                        min="5"
                                                        max="50"
                                                        value={pillar.frequency?.currentWeight || 25}
                                                        onChange={(e) => updateCustomizedPillar(idx, 'frequency.currentWeight', parseInt(e.target.value))}
                                                        style={{ width: '100%' }}
                                                    />
                                                </div>
                                                <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, minWidth: '40px', color: 'var(--color-text-primary)' }}>
                                                    {pillar.frequency?.currentWeight || 25}%
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Error message */}
                    {error && (
                        <div style={{
                            marginTop: 'var(--space-4)',
                            padding: 'var(--space-3)',
                            background: 'var(--color-error-bg)',
                            border: '1px solid var(--color-error)',
                            borderRadius: 'var(--radius-md)',
                            color: 'var(--color-error)',
                            fontSize: 'var(--font-size-sm)'
                        }}>
                            {error}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div style={{
                        padding: 'var(--space-4) var(--space-6)',
                        borderTop: '1px solid var(--color-border)',
                        display: 'flex',
                        justifyContent: 'space-between'
                    }}>
                        <button
                            onClick={currentStep === 0 ? onClose : handleBack}
                            className="btn btn-secondary"
                            disabled={isLoading}
                        >
                            <ChevronLeft size={16} />
                            {currentStep === 0 ? 'Cancel' : 'Back'}
                        </button>

                        <button
                            onClick={handleNext}
                            className="btn btn-primary"
                            disabled={isLoading || isGeneratingSuggestions || isTransitioning}
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 size={16} className="animate-spin" />
                                    Saving...
                                </>
                            ) : currentStep === 2 ? (
                                <>
                                    Complete Setup
                                    <Check size={16} />
                                </>
                            ) : (
                                <>
                                    Continue
                                    <ChevronRight size={16} />
                                </>
                            )}
                        </button>
                </div>
            </div>
        </div>
    );
};

export default StrategySetupWizard;
